import type { Note, ActiveDraft, SyncOperation } from '../domain/note'
import type { SettingsRecord } from '../domain/settings'
import { assetRequiresSynchronization, type NoteAsset } from '../assets/assetTypes'
import { shouldPersistActiveDraft } from '../sync/activeNoteSync'

export const DATA_CONTINUITY_SCHEMA_VERSION = 1 as const

export interface LocalDataContinuitySnapshot {
  schemaVersion: typeof DATA_CONTINUITY_SCHEMA_VERSION
  capturedAt: number
  notes: {
    total: number
    synchronized: number
    unsynchronized: number
    failed: number
    trashed: number
  }
  activeDraft: {
    present: boolean
    containsLocalChanges: boolean
  }
  outbox: {
    total: number
    failed: number
  }
  settings: {
    total: number
    unsynchronized: number
    failed: number
  }
  assets: {
    total: number
    synchronized: number
    unsynchronized: number
    failed: number
    remoteOnly: number
    residentBytes: number
    unsynchronizedBytes: number
  }
  binding: {
    accountBound: boolean
    environmentBound: boolean
    noteCursorPresent: boolean
    assetCursorPresent: boolean
  }
  hasUnsynchronizedWork: boolean
  safeForServerRestoreCutover: boolean
}

interface ContinuityInput {
  notes: Note[]
  activeDraft?: ActiveDraft
  outbox: SyncOperation[]
  settings: SettingsRecord[]
  assets: NoteAsset[]
  accountBound: boolean
  environmentBound: boolean
  noteCursorPresent: boolean
  assetCursorPresent: boolean
  now?: number
}

function assetBlobBytes(asset: NoteAsset): number {
  return (asset.sourceBlob?.size ?? 0)
    + (asset.processedMasterBlob?.size ?? 0)
    + (asset.thumbnailBlob?.size ?? 0)
}

function assetIsSynchronized(asset: NoteAsset): boolean {
  return !assetRequiresSynchronization(asset)
}

function settingNeedsSynchronization(record: SettingsRecord): boolean {
  return record.localSyncState !== 'synced'
    && !(record.recordType === 'global' && record.localSyncState === 'local' && record.modifiedAt === 0)
}

export function buildLocalDataContinuitySnapshot({
  notes,
  activeDraft,
  outbox,
  settings,
  assets,
  accountBound,
  environmentBound,
  noteCursorPresent,
  assetCursorPresent,
  now = Date.now(),
}: ContinuityInput): LocalDataContinuitySnapshot {
  const notesById = new Map(notes.map((note) => [note.id, note]))
  const activeDraftContainsLocalChanges = Boolean(
    activeDraft
    && shouldPersistActiveDraft(activeDraft, notesById.get(activeDraft.noteId)),
  )
  const unsynchronizedNotes = notes.filter((note) => note.localSyncState !== 'synced')
  const failedNotes = notes.filter((note) => note.localSyncState === 'failed')
  const unsynchronizedSettings = settings.filter(settingNeedsSynchronization)
  const failedSettings = settings.filter((record) => record.localSyncState === 'failed')
  const unsynchronizedAssets = assets.filter((asset) => !assetIsSynchronized(asset))
  const failedAssets = assets.filter((asset) => asset.state === 'error')
  const remoteOnlyAssets = assets.filter((asset) =>
    asset.state === 'synced'
    && asset.sourceBlob === null
    && asset.processedMasterBlob === null
    && asset.thumbnailBlob === null
    && Boolean(asset.thumbnailStoragePath ?? asset.masterStoragePath),
  )
  const failedOutbox = outbox.filter(
    (operation) => operation.attemptCount > 0 || operation.lastErrorCode !== null,
  )
  const hasUnsynchronizedWork = activeDraftContainsLocalChanges
    || unsynchronizedNotes.length > 0
    || outbox.length > 0
    || unsynchronizedSettings.length > 0
    || unsynchronizedAssets.length > 0

  return {
    schemaVersion: DATA_CONTINUITY_SCHEMA_VERSION,
    capturedAt: now,
    notes: {
      total: notes.length,
      synchronized: notes.length - unsynchronizedNotes.length,
      unsynchronized: unsynchronizedNotes.length,
      failed: failedNotes.length,
      trashed: notes.filter((note) => note.status === 'trash').length,
    },
    activeDraft: {
      present: Boolean(activeDraft),
      containsLocalChanges: activeDraftContainsLocalChanges,
    },
    outbox: {
      total: outbox.length,
      failed: failedOutbox.length,
    },
    settings: {
      total: settings.length,
      unsynchronized: unsynchronizedSettings.length,
      failed: failedSettings.length,
    },
    assets: {
      total: assets.length,
      synchronized: assets.length - unsynchronizedAssets.length,
      unsynchronized: unsynchronizedAssets.length,
      failed: failedAssets.length,
      remoteOnly: remoteOnlyAssets.length,
      residentBytes: assets.reduce((total, asset) => total + assetBlobBytes(asset), 0),
      unsynchronizedBytes: unsynchronizedAssets.reduce(
        (total, asset) => total + assetBlobBytes(asset),
        0,
      ),
    },
    binding: {
      accountBound,
      environmentBound,
      noteCursorPresent,
      assetCursorPresent,
    },
    hasUnsynchronizedWork,
    safeForServerRestoreCutover: !hasUnsynchronizedWork,
  }
}
